import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";

import XGSFormTextarea from "../../ui-components/form/textarea/xgsFormTextarea";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import { FeedbackModel, FeedbackSchema } from "../../app/data/feedback/models";
import FeedbackState from "../../slices/feedback/FeedbackState";
import {
  feedbackSelector,
  resetFeedbackState,
  submitJiraFeedback
} from "../../slices/feedback/feedbackSlice";
import XGSPopup, { PopupTheme } from "../../ui-components/xgs-popup/xgsPopup";
import { cn } from "../../services/common/className";
import Rating from "../../ui-components/rating/rating";
import { WizardCheck } from "../../ui-components/xgs-wizard/wizardCheck";
import AnnouncementTooltip from "../../ui-components/announcement-tooltip/announcementTooltip";
import XGSIcon from "../../ui-components/icon/xgsIcon";
import XGSIcons from "../../ui-components/icon/xgsIcons";
import "./feedback.scss";
import "./../../ui-components/xgs-wizard/wizard.scss";
import { useLocation } from "react-router-dom";

export interface FeedbackProps {
  mix?: string;
}

const initialValues: FeedbackModel = {
  rating: "0",
  comments: "",
  attachments: [],
};

const UPLOAD_MAX_SIZE = 4; // MB

const UPLOAD_FILE_TYPES = {
  "image/jpeg": [],
  "image/png": [],
  "application/pdf": [],
};


interface FeedbackFormProps {
  formikProps: FormikProps<FeedbackModel>;
  feedbackState: FeedbackState;
  onClose: () => void;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ formikProps, feedbackState, onClose }) => {
  const [fileError, setFileError] = useState<string>("");

  const { getRootProps, getInputProps } = useDropzone({
    accept: UPLOAD_FILE_TYPES,
    maxSize: UPLOAD_MAX_SIZE * 1048576,
    maxFiles: 3,
    disabled: (formikProps.values.attachments?.length ?? 0) >= 3,
    onDrop: (acceptedFiles, fileRejections) => {
      setFileError("");

      if (fileRejections?.length > 0) {
        fileRejections[0].errors.forEach((err) => {
          if (err.code === "file-too-large") {
            setFileError(`Files no larger than ${UPLOAD_MAX_SIZE} MB are allowed!`);
          }
          if (err.code === "file-invalid-type") {
            setFileError("Only images of certain formats are allowed!");
          }
          if (err.code === "too-many-files") {
            setFileError(`Maximum 3 files are allowed!`);
          }
        });
      }

      if (acceptedFiles.length === 0) return;

      const currentFiles = formikProps.values.attachments ?? [];
      const totalFiles = currentFiles.length + acceptedFiles.length;

      // Enforce cumulative max of 3
      if (totalFiles > 3) {
        setFileError(`Maximum 3 files are allowed!`);
        const remainingSlots = 3 - currentFiles.length;
        if (remainingSlots <= 0) return;
        // Optionally allow partial fill up to the limit:
        acceptedFiles = acceptedFiles.slice(0, remainingSlots);
      }

      formikProps.setFieldValue("attachments", [
        ...currentFiles,
        ...acceptedFiles,
      ]);
    },
  });

  return (
    <Form className="feedback__form">
      <Rating
        value={formikProps.values.rating}
        onChange={formikProps.handleChange}
      />

      <XGSFormTextarea
        name="comments"
        label="Comments"
        counter={500}
        required
      />

      <div
        {...getRootProps({
          className: cn("xgs-upload__area")(
            { "disabled": (formikProps.values.attachments?.length ?? 0) >= 3 }
          )
        })}
      >
        <input {...getInputProps()} />
        {fileError && <p>{fileError}</p>}

        <span className="blue-link">Tap, click or drag &amp; drop screenshots here</span>
        <div className="xgs-upload__notes">
          (<strong>PNG</strong>, <strong>JPEG</strong>, <strong>PDF</strong> only)
        </div>
      </div>

      {formikProps.values.attachments?.map((file, i) => (
        <div className="xgs-bol__return__reference__file" key={file.name + i}>
          <div className="xgs-bol__return__reference__file__name">{file.name}</div>
          <div
            className="xgs-bol__return__reference__file__delete"
            onClick={() =>
              formikProps.setFieldValue(
                "attachments",
                formikProps.values.attachments!.filter((_, idx) => idx !== i)
              )
            }
          >
            <XGSIcon icon={XGSIcons.faTimes} />
          </div>
        </div>
      ))}

      <div className="feedback__submit-buttons">
        <Button
          theme={ButtonThemes.gray}
          type="button"
          className=""
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button
          theme={ButtonThemes.blue}
          type="submit"
          className=""
          spinner={feedbackState.requestStarted}
          disabled={!formikProps.isValid || !formikProps.dirty}
        >
          Submit
        </Button>
      </div>
    </Form>
  );
};

const Feedback: React.FC<FeedbackProps> = (props) => {
  const feedbackState: FeedbackState = useSelector(feedbackSelector);
  const dispatch = useDispatch();
  const location = useLocation();

  const [isOpened, setIsOpened] = useState(false);

  const onSubmitFeedback = (data: FeedbackModel) => {
    dispatch(submitJiraFeedback(
      { ...data, page: window.location.href },
      () => { setTimeout(closeFeedback, 2000); },
      (error) => { toast.error(error); },
    ));
  };

  const closeFeedback = () => {
    setIsOpened(false);
    dispatch(resetFeedbackState());
  };

  return (
    <div className={cn("feedback")(null, [props.mix])}>
      {!isOpened && (
        <>
          {location.pathname.includes("/ai/shipment-chat") ? (
            <AnnouncementTooltip
              id="ai-chat-feedback-announcement"
              text="Please use the feedback form to suggest additional information you'd like to access through the AI chat."
            >
              <div className="feedback__button" onClick={() => setIsOpened(true)}>Feedback</div>
            </AnnouncementTooltip>
          ) : (
            <div className="feedback__button" onClick={() => setIsOpened(true)}>Feedback</div>
          )}
        </>
      )}

      <XGSPopup
        opened={isOpened}
        onClose={closeFeedback}
        title=""
        className="xgs-feedback-modal"
        hideCloseButton={true}
        theme={PopupTheme.NEW}
      >
        {!(feedbackState.requestSucceed && feedbackState.requestCreator === "SUBMIT") && (
          <>
            <div className="feedback__title">Feedback</div>
            <div className="feedback__subtitle">
              {`Please describe your experience with the page (${window.location.href}) and rate it.`}
            </div>
            <Formik
              onSubmit={onSubmitFeedback}
              initialValues={initialValues}
              validationSchema={FeedbackSchema}
              enableReinitialize
            >
              {(formikProps: FormikProps<FeedbackModel>) => (
                <FeedbackForm
                  formikProps={formikProps}
                  feedbackState={feedbackState}
                  onClose={closeFeedback}
                />
              )}
            </Formik>
          </>
        )}

        {feedbackState.requestSucceed && feedbackState.requestCreator === "SUBMIT" && (
          <div className="feedback__success">
            <WizardCheck />

            <div className="feedback__title">Thanks for feedback!</div>

            <Button
              theme={ButtonThemes.gray}
              type="button"
              className=""
              onClick={closeFeedback}
            >
              Close
            </Button>
          </div>
        )}
      </XGSPopup>
    </div>
  );
};

export default Feedback;